import React from 'react';
import {
  Box,
  Button,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../auth/AuthContext';
import config from '../../config';
import {
  asPrettyJSON,
  canonicalRequest,
  EditorPageShell,
  parseJSONObject,
  RemoteEntityAutocomplete,
  SectionCard,
} from './CanonicalEditorCommon';

const designStatuses = ['active', 'archived'];

function emptyForm() {
  return {
    filename: '',
    status: 'active',
    asset: null,
    metadataText: '{}',
  };
}

function formFromDesign(design) {
  return {
    filename: design.filename || '',
    status: design.status || 'active',
    asset: design.asset || (design.image_id ? {
      id: design.image_id,
      filename: design.filename,
      image_url: design.image_url,
      width: design.width,
      height: design.height,
      mime_type: design.mime_type,
      has_local_content: !design.image_url,
    } : null),
    metadataText: asPrettyJSON(design.metadata),
  };
}

function assetImageURL(asset) {
  if (asset?.image_url) {
    return asset.image_url;
  }
  return asset?.id ? `${config.apiBaseUrl}/images/${asset.id}/content` : '';
}

const CanonicalDesignEditor = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { fetchWithAuth } = useAuth();
  const isNew = !id || id === 'new';
  const [design, setDesign] = React.useState(null);
  const [form, setForm] = React.useState(emptyForm);
  const [loading, setLoading] = React.useState(!isNew);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState('');
  const [validation, setValidation] = React.useState(null);

  const loadDesign = React.useCallback(async () => {
    if (isNew) {
      setDesign(null);
      setForm(emptyForm());
      setLoading(false);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const payload = await canonicalRequest(fetchWithAuth, `/designs/${id}`);
      setDesign(payload.item);
      setForm(formFromDesign(payload.item));
      setValidation(null);
    } catch (loadError) {
      setError(loadError.message || 'Unable to load design');
    } finally {
      setLoading(false);
    }
  }, [fetchWithAuth, id, isNew]);

  React.useEffect(() => {
    loadDesign();
  }, [loadDesign]);

  const setField = (field, value) => setForm((current) => ({ ...current, [field]: value }));

  const save = async () => {
    setSaving(true);
    setError('');
    setValidation(null);
    try {
      const payload = {
        filename: form.filename.trim(),
        image_id: form.asset?.id || null,
        status: form.status,
        metadata: parseJSONObject(form.metadataText, 'Design metadata'),
        ...(!isNew ? { expected_version: design?.version } : {}),
      };
      const result = await canonicalRequest(fetchWithAuth, isNew ? '/designs' : `/designs/${id}`, {
        method: isNew ? 'POST' : 'PATCH',
        body: JSON.stringify(payload),
      });
      setValidation(result.validation || null);
      if (isNew) {
        navigate(`/designs/${result.id}`, { replace: true });
      } else {
        await loadDesign();
      }
    } catch (saveError) {
      setValidation(saveError.validation || null);
      setError(saveError.status === 409
        ? 'This design changed since you opened it. Reload before applying your edits.'
        : saveError.message || 'Unable to save design');
    } finally {
      setSaving(false);
    }
  };

  const validate = async () => {
    if (isNew) {
      setError('Save the design once before running a server validation check.');
      return;
    }
    setError('');
    try {
      const result = await canonicalRequest(fetchWithAuth, `/designs/${id}/validate`, { method: 'POST' });
      setValidation(result);
    } catch (validateError) {
      setValidation(validateError.validation || null);
      setError(validateError.message || 'Unable to validate design');
    }
  };

  const previewURL = assetImageURL(form.asset);

  return (
    <EditorPageShell
      backTo="/designs"
      eyebrow={isNew ? 'Create design' : 'Design'}
      title={form.filename || (isNew ? 'Untitled design' : 'Design')}
      subtitle={isNew
        ? 'Create reusable artwork locally without changing any fulfillment provider.'
        : design?.id}
      status={form.status}
      version={design?.version}
      loading={loading}
      saving={saving}
      error={error}
      validation={validation}
      onReload={isNew ? null : loadDesign}
      onValidate={validate}
      onSave={save}
      saveLabel={isNew ? 'Create design' : 'Save design'}
      extraActions={(
        <Button variant="outlined" startIcon={<CloudUploadIcon />} onClick={() => navigate('/upload')}>
          Upload asset
        </Button>
      )}
    >
      <Stack spacing={2}>
        <SectionCard
          title="Design identity"
          description="A design is reusable artwork that can be placed on many products and listings."
        >
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '2fr 1fr' }, gap: 2 }}>
            <TextField
              label="Design name"
              required
              value={form.filename}
              onChange={(event) => setField('filename', event.target.value)}
              helperText="Use the artwork name without a file extension."
            />
            <TextField select label="Lifecycle status" value={form.status} onChange={(event) => setField('status', event.target.value)}>
              {designStatuses.map((status) => <MenuItem key={status} value={status}>{status}</MenuItem>)}
            </TextField>
          </Box>
        </SectionCard>

        <SectionCard
          title="Source asset"
          description="Choose the reusable image file for this design. Assets remain local records until another workflow uses them."
        >
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1fr) 240px' }, gap: 2.5, alignItems: 'start' }}>
            <RemoteEntityAutocomplete
              fetchWithAuth={fetchWithAuth}
              endpoint="/assets"
              label="Image asset"
              value={form.asset}
              onChange={(asset) => setField('asset', asset)}
              getOptionLabel={(asset) => asset.filename || asset.id}
              helperText="Search by uploaded filename."
              required
            />
            <Box
              sx={{
                minHeight: 220,
                border: 1,
                borderColor: 'divider',
                borderRadius: 2,
                bgcolor: 'background.default',
                display: 'grid',
                placeItems: 'center',
                overflow: 'hidden',
              }}
            >
              {previewURL ? (
                <Box component="img" src={previewURL} alt="" sx={{ width: '100%', height: 220, objectFit: 'contain' }} />
              ) : (
                <Typography variant="body2" color="text.secondary">Select an asset to preview it.</Typography>
              )}
            </Box>
          </Box>
          {form.asset ? (
            <Typography variant="caption" color="text.secondary">
              {form.asset.mime_type || 'Image'}
              {form.asset.width && form.asset.height ? ` · ${form.asset.width} × ${form.asset.height}` : ''}
              {` · ${form.asset.id}`}
            </Typography>
          ) : null}
        </SectionCard>

        <SectionCard
          title="Metadata"
          description="Optional extension data for private workflows. Provider-specific values should use namespaced keys."
        >
          <TextField
            label="Metadata JSON"
            value={form.metadataText}
            onChange={(event) => setField('metadataText', event.target.value)}
            multiline
            minRows={6}
            fullWidth
            inputProps={{ spellCheck: false }}
          />
        </SectionCard>
      </Stack>
    </EditorPageShell>
  );
};

export default CanonicalDesignEditor;
